import { Label, Textarea, Button } from '@quadminds/mindkit';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRateConversation from '../../../../../hooks/useRateConversation';
import { useSendFeedbackMail } from '../../../../../hooks/useSendFeedbackMail';

type Message = {
    is_user: boolean;
    message: string;
    is_flagged?: boolean; // opcional
};

export type FeedbackConversation = {
    user_email: string;
    feedback_date: string;
    conversation_id: string;
    previous_messages: Message[];
    user_comments: string;
    is_impersonating?: boolean;
    original_user_id?: string;
    organization: string;
    typology: string;
    environment: string;
    agent_version: string;
};

interface FeedbackBoxProps {
    token: string;
    interactionId: string;
    resetRating: (justInputState?: boolean) => void;
    status: 'idle' | 'success' | 'error';
    updateStatus: (status: 'idle' | 'success' | 'error') => void;
    payload: FeedbackConversation;
}

const FeedbackBox: FC<FeedbackBoxProps> = ({ token, resetRating, updateStatus, status, payload, interactionId }) => {
    const [feedback, setFeedBack] = useState<string>('');
    const { mutateAsync, reset, status: requestStatus, isPending } = useSendFeedbackMail(token);
    const { mutateAsync: triggerRating } = useRateConversation();
    const { t } = useTranslation();
    const timer = useRef<NodeJS.Timeout | null>(null);
    const handleSendEmail = useCallback(() => {
        return mutateAsync({ ...payload, user_comments: feedback })
            .then(() => {
                if (feedback.length > 0) {
                    triggerRating({
                        conversationId: payload.conversation_id,
                        interactionId: interactionId,
                        rating: 'negative',
                        feedbackComment: feedback,
                        agent_version: payload.agent_version,
                    }).catch((error) => {
                        console.error('Error saving rating comments:', error);
                    });
                }
                timer.current = setTimeout(() => {
                    reset();
                    resetRating(true);
                }, 2500);
                setFeedBack('');
            })
            .catch((error) => {
                console.error('Error sending feedback:', error);
            });
    }, [feedback, interactionId, mutateAsync, payload, reset, resetRating, triggerRating]);

    useEffect(() => {
        if (requestStatus !== status && requestStatus !== 'pending') {
            updateStatus(requestStatus);
        }
    }, [requestStatus, status, updateStatus]);

    return (
        <div className="flex flex-col space-y-4 items-start w-full p-4  rounded-lg shadow">
            <div className="flex flex-col space-y-3 w-full">
                <Label htmlFor="comment">{t('chat.feedback.label')}</Label>
                <Textarea
                    value={feedback}
                    onChange={(e) => setFeedBack(e.target.value)}
                    id="comment"
                    placeholder={t('chat.feedback.placeholder')}
                    autoFocus
                    className="min-h-[80px] p-3"
                />
            </div>
            <div className="flex space-x-4 mt-2">
                <Button onClick={handleSendEmail} disabled={feedback.length < 1 || isPending}>
                    {t('chat.feedback.send')}
                </Button>
                <Button variant="ghost" onClick={() => resetRating(false)}>
                    {t('chat.feedback.cancel')}
                </Button>
            </div>
        </div>
    );
};

export default FeedbackBox;
